
/**
 * Shared utility for handling contact form submissions across the application
 */
import { getRecaptchaSiteKey, getRecaptchaScriptUrl, getApiBaseUrl } from './configService';

/**
 * Loads the reCAPTCHA v3 script if not already loaded
 * @returns Promise that resolves when the script is loaded
 */
const loadRecaptchaScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if script is already loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => resolve(true));
      return;
    }

    // Create script element
    const script = document.createElement('script');
    script.src = getRecaptchaScriptUrl();
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => resolve(true));
    };
    
    script.onerror = () => {
      console.error('Error loading reCAPTCHA script');
      // Fall back to allowing the submission without reCAPTCHA
      resolve(false);
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Get reCAPTCHA token for the specified action
 * @param action The action name for analytics
 * @returns Promise that resolves with the reCAPTCHA token
 */
const getRecaptchaToken = async (action: string): Promise<string | null> => {
  try {
    console.log('🔐 Attempting to load reCAPTCHA...');
    const scriptLoaded = await loadRecaptchaScript();
    
    if (!scriptLoaded || !window.grecaptcha) {
      console.warn('⚠️ reCAPTCHA not available, proceeding without verification');
      return null;
    }
    
    console.log('✅ reCAPTCHA loaded, getting token...');
    const token = await window.grecaptcha.execute(getRecaptchaSiteKey(), { action });
    console.log('✅ reCAPTCHA token obtained successfully');
    return token;
  } catch (error) {
    console.error('❌ Error getting reCAPTCHA token:', error);
    return null;
  }
};

/**
 * Validates a form submission for potential spam
 * @param data Form data that may contain a honeypot field
 * @returns Boolean indicating if the submission is valid (not spam)
 */
const validateHoneypot = (data: Record<string, any>): boolean => {
  // If website field exists and is filled, it's likely spam
  if (data.website !== undefined && data.website && data.website.length > 0) {
    console.log("🚫 Honeypot field filled - potential spam detected");
    return false;
  }
  return true;
};

// Track submission attempts to prevent rapid duplicates
const submissionTracker = new Map<string, number>();

/**
 * Check if this submission is too recent (prevents duplicates)
 */
const checkSubmissionCooldown = (email: string): boolean => {
  const now = Date.now();
  const lastSubmission = submissionTracker.get(email);
  
  if (lastSubmission && (now - lastSubmission) < 30000) { // 30 second cooldown
    console.warn('🚫 Submission too recent - cooldown active');
    return false;
  }
  
  submissionTracker.set(email, now);
  return true;
};

/**
 * Sends a form submission to the backend
 * @param data The form data to send
 * @param subject Optional subject line for the email
 * @param additionalInfo Optional additional information to include in the email
 * @param recipientEmail The email address to send the form submission to
 * @returns Promise that resolves when the submission is complete
 */
export const sendContactForm = async (
  data: Record<string, any>,
  subject: string = "Nieuw contact formulier",
  additionalInfo?: Record<string, any>,
  recipientEmail: string = 'bert@afsprakenmaker.be'
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📧 Starting contact form submission...');
    console.log('📋 Form data:', { ...data, website: '[HIDDEN]' }); // Hide honeypot
    console.log('📧 Recipient:', recipientEmail);
    console.log('📝 Subject:', subject);
    
    // Check honeypot field if it exists in the data
    if (data.website !== undefined && !validateHoneypot(data)) {
      console.log("🚫 Spam submission blocked by honeypot");
      // Return success to avoid showing error for potential spammers
      return { success: true };
    }
    
    // Check submission cooldown
    if (data.email && !checkSubmissionCooldown(data.email)) {
      return { 
        success: false, 
        error: 'Please wait 30 seconds before submitting again' 
      };
    }
    
    // Get reCAPTCHA token
    const recaptchaToken = await getRecaptchaToken('contact_form');
    console.log('🔐 reCAPTCHA status:', recaptchaToken ? 'Token obtained' : 'No token (proceeding anyway)');
    
    // Create a clean data object without the honeypot field
    const cleanData = { ...data };
    delete cleanData.website;
    
    // Prepare email body with all relevant information
    const emailBody = {
      subject: subject,
      message: formatEmailMessage(cleanData, additionalInfo),
      recaptchaToken: recaptchaToken,
      to: recipientEmail
    };
    
    console.log('🚀 Sending request to API...');
    const baseUrl = getApiBaseUrl();
    console.log('🌐 API URL:', `${baseUrl}/contactform`);
    
    // Send the actual request
    const response = await fetch(`${baseUrl}/contactform`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailBody),
    });
    
    console.log('📡 API Response Status:', response.status);
    console.log('📡 API Response Headers:', Object.fromEntries(response.headers.entries()));
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    let result;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      result = await response.json();
      console.log('✅ API JSON Response:', result);
    } else {
      const textResult = await response.text();
      console.log('✅ API Text Response:', textResult);
      // Try to parse as JSON in case content-type header is wrong
      try {
        result = JSON.parse(textResult);
        console.log('✅ Parsed as JSON:', result);
      } catch {
        // If not JSON, treat as success if we got a 200 response
        result = { success: true, message: textResult };
      }
    }
    
    // Check various possible success indicators
    const isSuccess = response.status === 200 || 
                     result?.success === true || 
                     result?.Code === 200 || 
                     result?.status === 'success' ||
                     (response.status >= 200 && response.status < 300);
    
    if (isSuccess) {
      console.log('✅ Email submission successful!');
      return { success: true };
    } else {
      console.error('❌ API indicated failure:', result);
      return { 
        success: false, 
        error: result?.message || result?.error || 'Unknown error from server' 
      };
    }
    
  } catch (error) {
    console.error("❌ Contact form submission failed:", error);
    
    // Log additional details for debugging
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error("🌐 Network error - API endpoint may be unreachable");
      return { 
        success: false, 
        error: 'Network error - please check your internet connection and try again' 
      };
    }
    
    if (error instanceof Error) {
      return { 
        success: false, 
        error: error.message 
      };
    }
    
    return { 
      success: false, 
      error: 'An unexpected error occurred. Please try again or contact us directly.' 
    };
  }
};

/**
 * Formats the email message from form data
 */
const formatEmailMessage = (data: Record<string, any>, additionalInfo?: Record<string, any>): string => {
  let message = `Nieuwe aanvraag voor potentiële klanten:\n\n`;
  
  // Add contact information
  if (data.voornaam || data.achternaam) {
    message += `Naam: ${data.voornaam || ''} ${data.achternaam || ''}\n`;
  }
  if (data.naam || data.name) message += `Naam: ${data.naam || data.name || 'Niet opgegeven'}\n`;
  if (data.bedrijfsnaam || data.bedrijf || data.company) {
    message += `Bedrijf: ${data.bedrijfsnaam || data.bedrijf || data.company || 'Niet opgegeven'}\n`;
  }
  if (data.btwNummer) message += `BTW Nummer: ${data.btwNummer}\n`;
  if (data.activiteit) message += `Activiteit: ${data.activiteit}\n`;
  if (data.email) message += `Email: ${data.email}\n`;
  if (data.telefoonnummer || data.phone || data.telefoon) {
    message += `Telefoonnummer: ${data.telefoonnummer || data.phone || data.telefoon}\n`;
  }
  if (data.postcode) message += `Postcode: ${data.postcode}\n`;
  if (data.bericht || data.message) message += `\nBericht:\n${data.bericht || data.message}\n`;
  
  // Add any additional information if provided
  if (additionalInfo && Object.keys(additionalInfo).length > 0) {
    message += `\nExtra informatie:\n`;
    Object.entries(additionalInfo).forEach(([key, value]) => {
      message += `${key}: ${value}\n`;
    });
  }
  
  message += `\nDeze aanvraag werd verzonden via het contact formulier op afsprakenmaker.be`;
  
  return message;
};

// Add type definition for global grecaptcha object
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export { validateHoneypot };
